import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { MapPin, ArrowRight, Truck, DollarSign, PhoneCall, Mountain } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useT } from '../utils/i18n';
const features = [{
  icon: Truck,
  title: 'Local Trucks, Faster Delivery',
  description: 'Our fleet is 30 minutes from your driveway. Same-day and next-day, no national queue.'
}, {
  icon: DollarSign,
  title: 'Direct From the Supplier',
  description: "No broker markup, no national middleman. You pay for the rock and the run — that's it."
}, {
  icon: PhoneCall,
  title: 'Real People on the Phone',
  description: 'Call once, get the dispatcher loading your order — not a ticket queue or an AI agent.'
}, {
  icon: Mountain,
  title: 'Knows the Northwest',
  description: 'We know what NW contractors actually spec — wet-soil base, drain rock, NW bark — so you order right the first time.'
}];
const container = {
  hidden: {
    opacity: 0
  },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};
const item = {
  hidden: {
    opacity: 0,
    y: 20
  },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: 'easeOut'
    }
  }
};
export function WhyUs() {
  const t = useT();
  return <section className="py-16 md:py-24 dark:bg-[#474443] transition-colors duration-300 border-y border-stone-200 dark:border-[#3f3d3c] bg-[#FCF9F2] relative overflow-hidden" id="why-us" data-id="element-1606">
      {/* Subtle decorative accent line */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-brand-orange/40 to-transparent" data-id="element-1607" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1608">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-start" data-id="element-1609">
          {/* LEFT COLUMN — Brand statement */}
          <motion.div initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-40px'
        }} transition={{
          duration: 0.6,
          ease: 'easeOut'
        }} className="lg:col-span-4 lg:sticky lg:top-24" data-id="element-1610">
            <div className="inline-flex items-center gap-2 mb-6" data-id="element-1611">
              <MapPin className="w-3.5 h-3.5 text-brand-orange" data-id="element-1612" />
              <span className="text-[11px] font-bold text-brand-orange uppercase tracking-[0.14em]" data-id="element-1613">
                {t('why.localOwned')}
              </span>
            </div>

            <h2 className="text-3xl md:text-4xl lg:text-[2.5rem] font-heading font-extrabold text-stone-900 dark:text-white leading-[1.1] mb-6 tracking-tight" data-id="element-1614">
              We're the dispatcher.
              <br data-id="element-1615" />
              We're the driver.
              <br data-id="element-1616" />
              We're local.
            </h2>

            <p className="text-stone-600 dark:text-stone-300 md:text-lg font-medium mb-8 leading-relaxed" data-id="element-1617">
              {t('why.localBody')}
            </p>
          </motion.div>

          {/* RIGHT COLUMN — Centered icon features + pill CTA */}
          <motion.div variants={container} initial="hidden" whileInView="show" viewport={{
          once: true,
          margin: '-40px'
        }} className="lg:col-span-8" data-id="element-1618">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-10 sm:gap-y-12 gap-x-8 mb-12 md:mb-14" data-id="element-1619">
              {features.map((feature, index) => <motion.div key={feature.title} variants={item} className="flex flex-col items-center text-center px-2" data-id="element-1620">
                  <div className="w-14 h-14 rounded-full bg-brand-orange/10 flex items-center justify-center mb-5 border border-brand-orange/20" data-id="element-1621">
                    <feature.icon className="w-6 h-6 text-brand-orange" strokeWidth={1.75} data-id="element-1622" />
                  </div>
                  <h3 className="font-heading text-base md:text-lg font-bold text-stone-900 dark:text-white mb-2.5 leading-tight" data-id="element-1623">
                    {feature.title}
                  </h3>
                  <p className="text-sm md:text-[15px] text-stone-600 dark:text-stone-300/90 leading-relaxed max-w-xs" data-id="element-1624">
                    {feature.description}
                  </p>
                </motion.div>)}
            </div>

            <motion.div variants={item} className="flex justify-center" data-id="element-1625">
              <Link to="/local-vs-national" className="group inline-flex items-center gap-2.5 px-7 py-3.5 rounded-full border border-brand-orange/40 hover:border-brand-orange text-brand-orange font-bold text-sm tracking-tight transition-all hover:bg-brand-orange/5" data-id="element-1626">
                See how we stack up vs. national marketplaces
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" data-id="element-1627" />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>;
}